import React from 'react';
import { differenceInDays } from 'date-fns';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import infoSvg from 'assets/icons/info.svg';
import shareSvg from 'assets/icons/share.svg';
import linkSvg from 'assets/icons/link.svg';
import Button, { Color } from 'lib/Button';
import Modal from 'lib/Modal';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import ToggleSwitch from 'lib/ToggleSwitch';
import Checkbox from 'lib/Checkbox';
import CopyToClipboardButton from '../../CopyToClipboardButton';
import { Error } from './Error';
import {
  ALLOCATION_METRICS,
  DISABLE_CAMPAIGN_PLAN_LINK_DESCRIPTION,
  DISABLE_CAMPAIGN_PLAN_LINK_MESSAGE,
  ENABLE_CAMPAIGN_PLAN_LINK_DESCRIPTION,
  GROUP_BY_OPTIONS,
  ShareCampaignPlanModalProps,
} from './ShareCampaignPlanModal.types';
import { CampaignPlanLinkDiscardModal } from './CampaignPlanLinkDiscardModal';
import { useShareCampaignPlanModalState } from './useShareCampaignPlanModalState';

const isDisableToggleVisible = (shareUrl: string, isSharingDisabled: boolean): boolean =>
  Boolean(shareUrl) || isSharingDisabled;

export const ShareCampaignPlanModal: React.FC<ShareCampaignPlanModalProps> = ({
  onGenerateLink,
  onUpdateLink,
  shareCampaignPlan: { error: shareError, isLoading: isShareLoading, url: shareUrl, expiresAt, disabledMessage },
  toggleCampaignPlan: { isChecked: isToggleChecked, isDisabled: isToggleDisabled, error: toggleError },
  onClose,
  onToggleSwitchChange,
  hasSelectedPois,
  initialCampaignPlanConfig,
  isConfigLoading,
  configLoadError,
}) => {
  const {
    campaignPlanConfig,
    hasConfigChanges,
    isDiscardModalOpen,
    setIsDiscardModalOpen,
    handleCloseModal,
    handleDiscardChanges,
    handleToggleMapChange,
    handleTogglePoiChange,
    handleGenerateLink,
    handleUpdateLink,
    handleMetricChange,
    isIncludeGroupBysEnabled,
    handleIncludeGroupBysChange,
    handleGroupByChange,
  } = useShareCampaignPlanModalState({
    initialCampaignPlanConfig,
    isConfigLoading,
    shareUrl,
    onClose,
    onGenerateLink,
    onUpdateLink,
  });

  return (
    <>
      <Modal icon={shareSvg} isOpen title="Share campaign" onClose={handleCloseModal}>
        <div className="flex flex-col gap-y-3 max-w-lg px-9">
          {isConfigLoading && (
            <div className="h-40">
              <CircularLoadingSpinner />
            </div>
          )}

          {!isConfigLoading && configLoadError && (
            <Error header="Unable to load campaign plan configuration" errorMessage={configLoadError} />
          )}

          {!isConfigLoading && !configLoadError && (
            <>
              <div>
                <h3 id="configure-allocation-report-heading" className="sub-header-base">
                  Configure Allocation Report
                </h3>
                <p className="body-base mt-1">Select which allocation metrics to include on the Campaign Plan.</p>

                <div className="mt-2" role="group" data-test-id="share-campaign-plan-allocation-metrics">
                  <div className="flex items-center body-base pb-1 justify-end gap-x-6">
                    <span id="planned-metrics-column-heading" className="body-base w-20 shrink-0 flex justify-center">
                      Planned
                    </span>
                  </div>
                  <ul className="flex flex-col">
                    {ALLOCATION_METRICS.map(({ id, label }) => (
                      <li key={id} className="flex items-center py-1 body-base justify-end gap-x-6">
                        <span className="flex-1">{label}</span>
                        <div className="w-20 shrink-0 flex justify-center">
                          <Checkbox
                            isSelected={campaignPlanConfig.allocationReport[id].planned}
                            onChange={handleMetricChange(id, 'planned')}
                            name={`planned-${id}`}
                            dataTestId={`allocation-report-metric-planned-${id}`}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div
                className="border-t border-neutral-950-opacity-10 pt-4 flex flex-col"
                data-test-id="share-campaign-plan-group-by-configuration"
              >
                <div className="flex items-center justify-between mb-3">
                  <p className="body-base">Include group by&apos;s</p>
                  <ToggleSwitch
                    isChecked={isIncludeGroupBysEnabled}
                    onChange={handleIncludeGroupBysChange}
                    dataTestId="share-campaign-plan-include-group-by-toggle"
                  />
                </div>
                {isIncludeGroupBysEnabled && (
                  <div className="flex flex-wrap gap-2">
                    {GROUP_BY_OPTIONS.map(({ code, name }) => (
                      <Checkbox
                        key={code}
                        label={name}
                        isSelected={campaignPlanConfig.allocationReport.allowedGroupByOptions.includes(code)}
                        onChange={() => handleGroupByChange(code)}
                        name={`group-by-${code}`}
                        dataTestId={`group-by-${code}`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div
                className="border-t border-neutral-950-opacity-10 pt-4"
                data-test-id="share-campaign-plan-map-configuration"
              >
                <h3 id="configure-map-heading" className="sub-header-base">
                  Configure Map
                </h3>
                <p className="body-base mt-1">Select the Campaign Plan Map configuration for the Campaign Plan.</p>
                <div className="mt-4 flex flex-col gap-y-3 pb-4 border-b border-neutral-950-opacity-10">
                  <div className="flex items-center justify-between body-base">
                    <span id="include-map-label">Include Map</span>
                    <ToggleSwitch
                      isChecked={campaignPlanConfig.map.includeMap}
                      onChange={handleToggleMapChange}
                      dataTestId="share-campaign-plan-include-map-toggle"
                    />
                  </div>
                  {hasSelectedPois && (
                    <div className="flex items-center justify-between body-base">
                      <span id="include-pois-label">Include POIs</span>
                      <ToggleSwitch
                        isChecked={campaignPlanConfig.map.includePOIs}
                        onChange={handleTogglePoiChange}
                        dataTestId="share-campaign-plan-include-pois-toggle"
                        isDisabled={!campaignPlanConfig.map.includeMap}
                      />
                    </div>
                  )}
                </div>
              </div>

              {shareError && <Error header="Unable to generate campaign plan link" errorMessage={shareError} />}

              {isShareLoading && (
                <div className="h-40">
                  <CircularLoadingSpinner />
                </div>
              )}

              {!shareUrl && !isToggleChecked && (
                <Button
                  color={Color.PRIMARY}
                  label="Generate link"
                  onClick={handleGenerateLink}
                  svg={linkSvg}
                  classNames="my-2"
                  isDisabled={isShareLoading}
                />
              )}

              {!isShareLoading && !shareError && shareUrl && (
                <>
                  <Button
                    color={Color.PRIMARY}
                    label="Update link"
                    svg={linkSvg}
                    classNames="my-2"
                    onClick={handleUpdateLink}
                    isDisabled={!hasConfigChanges}
                  />

                  <div
                    className="flex flex-row items-center gap-x-2 w-full min-w-0"
                    data-test-id="share-campaign-plan-url-section"
                  >
                    <div
                      className={cx('flex-1 min-w-0 border rounded-md border-neutral-950-opacity-10 px-3 py-2', {
                        'bg-neutral-950-opacity-5 pointer-events-none select-none selection:bg-transparent':
                          isToggleChecked,
                      })}
                    >
                      <div className="text-neutral-600 body-base truncate" title={shareUrl}>
                        {shareUrl}
                      </div>
                    </div>
                    <div className="shrink-0">
                      <CopyToClipboardButton
                        color={Color.PRIMARY}
                        label="Copy"
                        textToCopy={shareUrl}
                        isDisabled={isToggleChecked}
                      />
                    </div>
                  </div>
                  <div className="flex flex-row items-center gap-x-2 body-base leading-6 text-neutral-900">
                    <SVG src={infoSvg} className="w-6 h-6 fill-current text-neutral-700" />
                    <p>
                      The campaign plan link will expire in {differenceInDays(new Date(expiresAt), new Date())} days.
                    </p>
                  </div>
                </>
              )}

              {isDisableToggleVisible(shareUrl, isToggleChecked) && (
                <div
                  className="flex flex-row border rounded-md border-neutral-950-opacity-10 p-3 space-x-3"
                  aria-disabled={isToggleDisabled}
                >
                  <ToggleSwitch
                    isChecked={isToggleChecked}
                    isDisabled={isToggleDisabled}
                    onChange={() => onToggleSwitchChange(campaignPlanConfig)}
                    dataTestId="share-campaign-plan-modal-disable-switch"
                  />

                  <div className="flex flex-col space-y-1 body-base">
                    <div>{disabledMessage || DISABLE_CAMPAIGN_PLAN_LINK_MESSAGE}</div>
                    <div className="text-neutral-900">
                      {disabledMessage ? ENABLE_CAMPAIGN_PLAN_LINK_DESCRIPTION : DISABLE_CAMPAIGN_PLAN_LINK_DESCRIPTION}
                    </div>

                    {toggleError && (
                      <Error
                        header={`Unable to ${isToggleChecked ? 'enable' : 'disable'} campaign plan`}
                        errorMessage={toggleError}
                      />
                    )}
                  </div>
                </div>
              )}
              {!isToggleChecked && <div className="body-base">This read-only link will be accessible by anyone.</div>}
            </>
          )}
        </div>
      </Modal>

      <CampaignPlanLinkDiscardModal
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
};
